import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';

import NotFoundPage from '../components/404';
import PageData from '../helpers/pageData';
import Layout from '../components/Layouts/Layout';
import Body from '../components/ui/Body/Body';
import getPageContextFromData from '../lib/dto/getPageContextFromData';

const DisciplinePage = ({
  data: { prismicDisciplinePage: { type, uid, data } },
}) => {
  if (!data) return <NotFoundPage />;

  const dataContext = getPageContextFromData(type, uid, data);

  return (
    <PageData.Provider value={dataContext}>
      <Layout>
        <Body />
      </Layout>
    </PageData.Provider>
  );
};

export const query = graphql`
query DisciplinePageBySlug($uid: StringQueryOperatorInput = {}) {
  prismicDisciplinePage(uid: $uid) {
    _previewable
    uid
    type
    data {
      introduction
      page_colour
      page_short_description
      prismic_page_name
      seo_description
      seo_title
      title
      thumbnail_image {
        localFile {
          publicURL
          extension
          childImageSharp {
            gatsbyImageData(
              layout: CONSTRAINED,
              quality: 100,
              width: 640,
              breakpoints: [640, 960, 1400]
            )
          }
        }
      }
      landscape_image {
        localFile {
          publicURL
          extension
          childImageSharp {
            gatsbyImageData(
              layout: CONSTRAINED,
              quality: 100,
              width: 1400,
              breakpoints: [640, 960, 1400]
            )
          }
        }
      }
      body {
        ... on PrismicDisciplinePageDataBodyContent {
          id
          slice_type
          primary {
            content_heading {
              html
              text
            }
            content_subtitle {
              richText
              html
              text
            }
            content_text {
              richText
              text
            }
            layout
          }
        }
        ... on PrismicDisciplinePageDataBodyImage {
          id
          slice_type
          primary {
            alignment
            image {
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED,
                    quality: 100,
                    width: 1400,
                    breakpoints: [640, 960, 1400]
                  )
                }
              }
            }
          }
        }
        ... on PrismicDisciplinePageDataBodyDivider {
          id
          slice_type
        }
        ... on PrismicDisciplinePageDataBodyServicesSection {
          id
          slice_type
          primary {
            heading {
              html
              text
            }
            image_caption_heading {
              html
              text
            }
            image_caption_description {
              richText
              text
            }
            image {
              url
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED,
                    quality: 100,
                    width: 1400,
                    breakpoints: [640, 960, 1400]
                  )
                }
              }
            }
          }
          items {
            service {
              document {
                ... on PrismicServicePost {
                  uid
                  data {
                    introduction
                    title
                  }
                }
              }
            }
          }
        }
        ... on PrismicDisciplinePageDataBodyHeroLink {
          id
          slice_type
          primary {
            ...DisciplinePageBodyHeroLink
          }
        }
        ... on PrismicDisciplinePageDataBodyExternalLinks {
          id
          slice_type
          primary {
            heading {
              html
              text
            }
          }
          items {
            link_date
            link_description {
              richText
              text
            }
            link_heading {
              text
            }
            link_url {
              url
            }
          }
        }
        ... on PrismicDisciplinePageDataBodyRelatedSubjectMatter {
          id
          slice_type
          primary {
            heading {
              html
              text
            }
          }
          items {
            related_content {
              document {
                ... on PrismicBlog {
                  id
                  uid
                  type
                  data {
                    title
                    thumbnail_image {
                      localFile {
                        publicURL
                        extension
                        childImageSharp {
                          gatsbyImageData(
                            layout: CONSTRAINED,
                            quality: 100,
                            width: 640,
                            breakpoints: [640, 960, 1400]
                          )
                        }
                      }
                    }
                    introduction
                  }
                }
                ... on PrismicCaseStudy {
                  id
                  uid
                  type
                  data {
                    title
                    introduction
                    thumbnail_image {
                      localFile {
                        publicURL
                        extension
                        childImageSharp {
                          gatsbyImageData(
                            layout: CONSTRAINED,
                            quality: 100,
                            width: 640,
                            breakpoints: [640, 960, 1400]
                          )
                        }
                      }
                    }
                  }
                }
                ... on PrismicEvent {
                  id
                  uid
                  type
                  data {
                    title
                    introduction
                    thumbnail_image {
                      localFile {
                        publicURL
                        extension
                        childImageSharp {
                          gatsbyImageData(
                            layout: CONSTRAINED,
                            quality: 100,
                            width: 640,
                            breakpoints: [640, 960, 1400]
                          )
                        }
                      }
                    }
                  }
                }
                ... on PrismicReport {
                  id
                  uid
                  type
                  data {
                    title
                    introduction
                    thumbnail_image {
                      localFile {
                        publicURL
                        extension
                        childImageSharp {
                          gatsbyImageData(
                            layout: CONSTRAINED,
                            quality: 100,
                            width: 640,
                            breakpoints: [640, 960, 1400]
                          )
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        ... on PrismicDisciplinePageDataBodyHubCollection {
          id
          slice_type
          primary {
            items_to_show
          }
          items {
            featured_content {
              id
              uid
              type
              document {
                ... on PrismicBlog {
                  id
                  uid
                  type
                  data {
                    title
                    thumbnail_image {
                      localFile {
                        publicURL
                        extension
                        childImageSharp {
                          gatsbyImageData(
                            layout: CONSTRAINED,
                            quality: 100,
                            width: 640,
                            breakpoints: [640, 960, 1400]
                          )
                        }
                      }
                    }
                  }
                }
                ... on PrismicCaseStudy {
                  id
                  uid
                  type
                  data {
                    title
                    thumbnail_image {
                      localFile {
                        publicURL
                        extension
                        childImageSharp {
                          gatsbyImageData(
                            layout: CONSTRAINED,
                            quality: 100,
                            width: 640,
                            breakpoints: [640, 960, 1400]
                          )
                        }
                      }
                    }
                  }
                }
                ... on PrismicEvent {
                  id
                  uid
                  type
                  data {
                    title
                    thumbnail_image {
                      localFile {
                        publicURL
                        extension
                        childImageSharp {
                          gatsbyImageData(
                            layout: CONSTRAINED,
                            quality: 100,
                            width: 640,
                            breakpoints: [640, 960, 1400]
                          )
                        }
                      }
                    }
                  }
                }
                ... on PrismicReport {
                  id
                  uid
                  type
                  data {
                    title
                    thumbnail_image {
                      localFile {
                        publicURL
                        extension
                        childImageSharp {
                          gatsbyImageData(
                            layout: CONSTRAINED,
                            quality: 100,
                            width: 640,
                            breakpoints: [640, 960, 1400]
                          )
                        }
                      }
                    }
                  }
                }
                ... on PrismicServicePost {
                  id
                  uid
                  type
                  data {
                    title
                    thumbnail_image {
                      localFile {
                        publicURL
                        extension
                        childImageSharp {
                          gatsbyImageData(
                            layout: CONSTRAINED,
                            quality: 100,
                            width: 640,
                            breakpoints: [640, 960, 1400]
                          )
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        ... on PrismicDisciplinePageDataBodyDownloadFormHs {
          id
          slice_type
          primary {
            form_title
            hubspot_form_id
            form_image {
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED,
                    quality: 100,
                    width: 1400,
                    breakpoints: [640, 960, 1400]
                  )
                }
              }
            }
          }
        }
        ... on PrismicDisciplinePageDataBodyTeamMembers {
          id
          slice_type
          primary {
            heading {
              text
            }
            sort
          }
          items {
            team_member {
              document {
                ... on PrismicTeamMember {
                  id
                  data {
                    name
                    title
                  }
                }
              }
            }
          }
        }
        ... on PrismicDisciplinePageDataBodyActionButton {
          id
          slice_type
          primary {
            layout
            button_text {
              text
            }
            button_url {
              id
              link_type
              isBroken
              type
              uid
              url
              target
            }
          }
        }
      }
    }
  }
}`;

DisciplinePage.propTypes = {
  data: PropTypes.shape({
    prismicDisciplinePage: PropTypes.object.isRequired,
  }).isRequired,
};

export default withPrismicPreview(DisciplinePage);
